/**
 * Shared presentation primitives for the LiteLLM plugin.
 *
 * The plugin renders inside whatever Backstage theme the host app supplies, so
 * everything here derives its colour from `--bui-*` tokens rather than
 * hard-coded values. The one exception is the chart series palette: Recharts
 * has no theme awareness, and its stock colours (#8884d8 / #82ca9d / #ffc658)
 * read as a demo rather than a product, so we ship a curated ramp that holds up
 * on both light and dark surfaces.
 */
import type { CSSProperties, ReactNode } from 'react';
import { Text } from '@backstage/ui';
import styles from './ui.module.css';

// ── formatting ────────────────────────────────────────────────────────────────

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** `2026-08-16` → `Aug 16`. Falls back to the raw value for anything else. */
export const fmtDateShort = (value: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  if (!m) return value;
  return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}`;
};

/** 54253484 → `54.3M`. Keeps axis labels from swallowing the plot area. */
export const fmtCompact = (n: number): string => {
  const v = n ?? 0;
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${(v / 1e9).toFixed(abs >= 1e10 ? 0 : 1)}B`;
  if (abs >= 1e6) return `${(v / 1e6).toFixed(abs >= 1e7 ? 0 : 1)}M`;
  if (abs >= 1e3) return `${(v / 1e3).toFixed(abs >= 1e4 ? 0 : 1)}K`;
  return String(Math.round(v));
};

/** Compact currency for axis ticks — `$24`, `$1.2K`. */
export const fmtUsdCompact = (n: number): string => {
  const v = n ?? 0;
  if (Math.abs(v) >= 1000) return `$${fmtCompact(v)}`;
  if (Math.abs(v) >= 10) return `$${v.toFixed(0)}`;
  return `$${v.toFixed(2)}`;
};

// ── chart palette ─────────────────────────────────────────────────────────────

/** Categorical ramp, ordered so neighbouring series stay distinguishable. */
export const CHART_COLORS = [
  '#6366F1', // indigo
  '#14B8A6', // teal
  '#F59E0B', // amber
  '#EC4899', // pink
  '#38BDF8', // sky
  '#84CC16', // lime
  '#F97316', // orange
  '#A855F7', // violet
] as const;

/** Fixed colours for series that carry meaning rather than identity. */
export const SERIES = {
  input: '#6366F1',
  output: '#14B8A6',
  success: '#0d9488',
  failure: '#ea580c',
  spend: '#F59E0B',
  budget: '#ea580c',
} as const;

const OTHER_COLOR = '#94A3B8';

/** Stable colour per series name, so a model keeps its colour across charts. */
export function chartColor(name: string): string {
  if (name === 'Other') return OTHER_COLOR;
  let h = 5381;
  for (let i = 0; i < name.length; i++)
    h = ((h << 5) + h + name.charCodeAt(i)) >>> 0;
  return CHART_COLORS[h % CHART_COLORS.length];
}

// ── chart chrome ──────────────────────────────────────────────────────────────

/**
 * Axis ticks, grid rules and the hover cursor.
 *
 * Recharts writes colours into SVG presentation attributes, which do not
 * resolve `var()`, so these cannot be `--bui-*` tokens. They are translucent
 * neutrals rather than fixed greys: composited over whatever surface the host
 * theme paints, one value reads correctly on light and dark alike. That is also
 * why there is nothing to subscribe to — the series palette above is likewise
 * fixed, so no part of a chart changes when the host switches theme.
 */
const CHROME = {
  /** Axis labels and legend text — matches a muted body colour on any surface. */
  tick: 'rgba(140, 140, 142, 0.95)',
  /** Grid rules and axis lines, at hairline weight. */
  line: 'rgba(140, 140, 142, 0.28)',
  /** Column/row highlight behind the tooltip. */
  cursor: 'rgba(140, 140, 142, 0.12)',
} as const;

/** Axis / grid / legend props shared by every chart. */
export const chartTheme = {
  grid: {
    stroke: CHROME.line,
    strokeDasharray: '2 4',
    vertical: false,
  },
  axis: {
    tick: { fontSize: 11, fill: CHROME.tick },
    tickLine: false,
    axisLine: { stroke: CHROME.line },
  },
  legend: {
    wrapperStyle: {
      fontSize: 11,
      color: CHROME.tick,
      paddingTop: 8,
    },
    iconType: 'circle' as const,
    iconSize: 8,
  },
  cursor: { fill: CHROME.cursor },
} as const;

// ── chart tooltip ─────────────────────────────────────────────────────────────

interface ChartTooltipProps {
  active?: boolean;
  label?: string | number;
  payload?: Array<{
    name?: string;
    value?: number;
    color?: string;
    dataKey?: string | number;
  }>;
  /** Formats each series value; defaults to a localised integer. */
  valueFormatter?: (value: number) => string;
  /** Formats the header; defaults to the short date form. */
  labelFormatter?: (label: string) => string;
  /** Drop zero-valued series — useful for stacked charts with many series. */
  hideZero?: boolean;
}

/**
 * Recharts' default tooltip is an opaque white card with a hard border — it
 * disappears on dark themes. This one uses the host's own surface tokens.
 */
export const ChartTooltip = ({
  active,
  label,
  payload,
  valueFormatter = (v: number) => v.toLocaleString(),
  labelFormatter = fmtDateShort,
  hideZero = false,
}: ChartTooltipProps) => {
  if (!active || !payload?.length) return null;
  const rows = payload.filter(
    p =>
      p.value !== undefined && p.value !== null && (!hideZero || p.value !== 0),
  );
  if (!rows.length) return null;
  return (
    <div className={styles.tooltip}>
      <Text variant="body-x-small" className={styles.tooltipLabel}>
        {typeof label === 'string' ? labelFormatter(label) : label}
      </Text>
      {rows.map((row, i) => (
        <div
          key={`${row.dataKey ?? row.name ?? i}`}
          className={styles.tooltipRow}
        >
          <span
            className={styles.swatch}
            style={{ '--swatch-color': row.color } as CSSProperties}
          />
          <Text variant="body-x-small" className={styles.tooltipName}>
            {row.name}
          </Text>
          <Text variant="body-x-small" className={styles.tooltipValue}>
            {valueFormatter(row.value as number)}
          </Text>
        </div>
      ))}
    </div>
  );
};

/**
 * Colour key rendered outside the SVG. Recharts' own `<Legend>` cannot
 * ellipsize, so a handful of fully-qualified model names
 * ("bedrock/eu.anthropic.…") wrap into a ragged block that eats the plot area.
 */
export const SeriesLegend = ({
  series,
}: {
  series: Array<{ name: string; color: string }>;
}) => (
  <div className={styles.legend}>
    {series.map(s => (
      <div key={s.name} className={styles.legendItem}>
        <span
          className={styles.swatch}
          style={{ '--swatch-color': s.color } as CSSProperties}
        />
        <Text
          variant="body-x-small"
          title={s.name}
          className={styles.legendName}
        >
          {s.name}
        </Text>
      </div>
    ))}
  </div>
);

// ── status pills ──────────────────────────────────────────────────────────────

export type Tone =
  | 'neutral'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'accent';

/**
 * A soft status badge. Deliberately *not* a filled `Tag` — host themes tend to
 * render those as saturated pills, and a table with one screaming orange pill
 * per row is unreadable. Tinted background, hairline border, squared corners.
 */
export const StatusPill = ({
  label,
  tone = 'neutral',
  dot = true,
  title,
  className,
}: {
  label: ReactNode;
  tone?: Tone;
  /** Show the leading tone dot. */
  dot?: boolean;
  title?: string;
  className?: string;
}) => (
  <span
    data-tone={tone}
    title={title}
    className={className ? `${styles.pill} ${className}` : styles.pill}
  >
    {dot && <span className={styles.pillDot} />}
    <span className={styles.pillLabel}>{label}</span>
  </span>
);

/** Quiet monospace tag for identifiers: model names, team slugs, roles. */
export const TagChip = ({
  label,
  title,
  mono = true,
}: {
  label: ReactNode;
  title?: string;
  mono?: boolean;
}) => (
  <span
    title={title}
    className={mono ? `${styles.tag} ${styles.tagMono}` : styles.tag}
  >
    {label}
  </span>
);

// ── surfaces ──────────────────────────────────────────────────────────────────

/** Page-level surface with a consistent title row. */
export const SectionCard = ({
  title,
  subtitle,
  description,
  actions,
  flush = false,
  children,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  /** One-line explainer under the title row. */
  description?: ReactNode;
  actions?: ReactNode;
  /** Set when the body renders its own edge-to-edge content (e.g. a table). */
  flush?: boolean;
  children: ReactNode;
}) => (
  <div className={styles.card}>
    {(title || actions) && (
      <div className={styles.cardHead}>
        <div style={{ minWidth: 0 }}>
          <Text variant="title-x-small" className={styles.cardTitle}>
            {title}
          </Text>
          {subtitle && (
            <Text variant="body-x-small" className={styles.cardSubtitle}>
              {subtitle}
            </Text>
          )}
          {/* Wrapped: BUI's Text resets margin, so spacing lives on the div. */}
          {description && (
            <div className={styles.cardDescription}>
              <Text variant="body-small" color="secondary">
                {description}
              </Text>
            </div>
          )}
        </div>
        {actions && <div className={styles.cardActions}>{actions}</div>}
      </div>
    )}
    {flush ? (
      children
    ) : (
      <div
        className={
          title
            ? styles.cardBody
            : `${styles.cardBody} ${styles.cardBodyTopPad}`
        }
      >
        {children}
      </div>
    )}
  </div>
);

/** Bordered frame around a single chart, with its own caption row. */
export const ChartCard = ({
  title,
  meta,
  height = 240,
  children,
}: {
  title: ReactNode;
  meta?: ReactNode;
  height?: number;
  children: ReactNode;
}) => (
  <div className={styles.chartCard}>
    <div className={styles.chartHead}>
      <Text variant="body-small" className={styles.eyebrow}>
        {title}
      </Text>
      {meta && (
        <Text variant="body-x-small" color="secondary">
          {meta}
        </Text>
      )}
    </div>
    <div
      className={styles.chartBody}
      style={{ '--chart-height': `${height}px` } as CSSProperties}
    >
      {children}
    </div>
  </div>
);

// ── metrics ───────────────────────────────────────────────────────────────────

export interface MetricDef {
  label: string;
  value: string;
  hint?: string;
  tone?: Tone;
}

/**
 * KPI row rendered as one framed strip divided into cells, rather than four
 * loose cards. Cells share a baseline grid so the numbers line up even when
 * only some of them carry a hint.
 */
export const MetricStrip = ({
  metrics,
  bare = false,
}: {
  metrics: MetricDef[];
  /** Drop the outer frame when the strip fills a flush SectionCard. */
  bare?: boolean;
}) => (
  <div
    className={
      bare ? `${styles.metrics} ${styles.metricsBare}` : styles.metrics
    }
    style={{ '--metric-count': metrics.length } as CSSProperties}
  >
    {metrics.map(m => (
      <div
        key={m.label}
        className={styles.metricCell}
        data-tone={m.tone ?? 'accent'}
      >
        <div className={styles.metricLabel}>
          <span className={styles.metricDot} />
          <Text variant="body-x-small" className={styles.eyebrow}>
            {m.label}
          </Text>
        </div>
        <div className={styles.metricValue}>{m.value}</div>
        <Text variant="body-x-small" className={styles.metricHint}>
          {m.hint ?? ' '}
        </Text>
      </div>
    ))}
  </div>
);

/** Label + value pair used inside cards. */
export const Stat = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className={styles.stat}>
    <Text variant="body-x-small" className={styles.eyebrow}>
      {label}
    </Text>
    <Text variant="body-small" className={styles.statValue}>
      {value}
    </Text>
  </div>
);

/**
 * Progress meter with an explicitly drawn track — a tinted track derived from
 * the bar colour makes an empty bar look full.
 */
export const Meter = ({
  value,
  tone = 'accent',
  height = 5,
  ariaLabel,
}: {
  value: number;
  tone?: Tone;
  height?: number;
  ariaLabel?: string;
}) => {
  const clamped = Math.max(0, Math.min(100, value));
  const isOverCap = value > 100;
  return (
    <div>
      <div
        className={styles.meter}
        data-tone={tone}
        style={{ '--meter-height': `${height}px` } as CSSProperties}
        role="meter"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={ariaLabel}
        aria-valuetext={
          isOverCap ? `${Math.round(value)}% — over cap` : undefined
        }
      >
        <div
          className={styles.meterFill}
          style={{ '--meter-value': `${clamped}%` } as CSSProperties}
        />
      </div>
      {/* Spelled out so "over cap" never depends on colour alone. */}
      {isOverCap && <div className={styles.meterOver}>Over cap</div>}
    </div>
  );
};

// ── segmented control ─────────────────────────────────────────────────────────

/**
 * Secondary navigation inside a card. Hand-rolled rather than a second set of
 * `Tabs` so it reads as subordinate to the page tabs instead of competing
 * with them.
 */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (value: T) => void;
  options: Array<{ value: T; label: string }>;
}) {
  return (
    <div role="tablist" className={styles.segmented}>
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          role="tab"
          aria-selected={opt.value === value}
          onClick={() => onChange(opt.value)}
          className={styles.segment}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── tables ────────────────────────────────────────────────────────────────────

/**
 * Shared table chrome: quiet uppercase headers, hairline row rules, a hover
 * highlight, and tabular figures so numeric columns align. Applied to a plain
 * `<table>` — BUI's `Table` is built around `useTable`'s own data model, which
 * these hand-built, expandable rows do not fit.
 */
export const tableClasses = {
  /** Goes on the `<table>` itself. */
  table: styles.dataTable,
  /** Wrapper that keeps a wide table scrolling inside the card. */
  scroll: styles.tableScroll,
  /** Per-cell opt-in for right-aligned numeric columns. */
  numeric: styles.numeric,
};

/** Centered empty / loading state for a card body. */
export const EmptyState = ({
  message,
  hint,
  height,
  action,
}: {
  message: string;
  hint?: string;
  height?: number;
  action?: ReactNode;
}) => (
  <div className={styles.empty} style={height ? { height } : undefined}>
    <Text variant="body-small" color="secondary">
      {message}
    </Text>
    {hint && (
      <Text variant="body-x-small" color="secondary">
        {hint}
      </Text>
    )}
    {action}
  </div>
);
